import { z } from 'zod';

import { defineFlowSpec } from '@elara/core/flows';
import { zFirestamp } from '@elara/firebolt/zod-types';

import { zToolCategory } from '#models/ChatMessageModelSpec.js';
import { DebugToolDefinitionSchema } from '#models/schemas/DebugToolDefinitionSchema.js';
import { MessagePartSchema } from '#models/schemas/message-schemas.js';

/**
 * The contract for the "getSystemStatus" flow.
 * @param output - System status including environment information.
 */
export const DebugGetSystemStatusFlowSpec = defineFlowSpec({
  name: 'DebugGetSystemStatus',
  privileged: true,
  output: z
    .object({
      authenticated: z.boolean(),
      userId: z.string().optional(),
      userEmail: z.string().optional(),
      timestamp: z.string(),
      environment: z.object({
        nodeVersion: z.string(),
        platform: z.string(),
        memoryUsage: z.any(),
        uptime: z.number(),
      }),
      env: z.record(z.string(), z.any()),
      firebase: z.object({ projectId: z.string(), region: z.string() }),
      tools: z.object({ available: z.boolean(), categories: z.array(z.string()) }),
    })
    .passthrough(),
});

export type DebugGetSystemStatusFlowSpec = typeof DebugGetSystemStatusFlowSpec;

/**
 * The contract for the "getChatState" flow.
 * @param input - Chat ID to inspect.
 * @param output - Chat state including messages and statistics.
 */
export const DebugGetChatStateFlowSpec = defineFlowSpec({
  name: 'DebugGetChatState',
  privileged: true,
  input: z.object({ chatId: z.string() }),
  output: z
    .object({
      chat: z.any(), // Chat data
      messages: z.array(z.any()), // Last 20 messages
      stats: z.object({
        totalMessages: z.number(),
        userMessages: z.number(),
        modelMessages: z.number(),
        toolCalls: z.number(),
      }),
      systemPrompt: z.array(MessagePartSchema),
      agentId: z.string().optional(),
      lastActivity: zFirestamp().or(z.string()),
    })
    .passthrough(),
});

export type DebugGetChatStateFlowSpec = typeof DebugGetChatStateFlowSpec;

export const DebugGetToolsResponseSchema = z.object({
  tools: z.array(DebugToolDefinitionSchema),
  count: z.number(),
  categories: z.array(zToolCategory),
  context: z.record(z.string(), z.any()),
});
export type DebugGetToolsResponseSchema = typeof DebugGetToolsResponseSchema;
export type DebugGetToolsResponse = z.infer<DebugGetToolsResponseSchema>;

/**
 * The contract for the "getTools" flow.
 * @param input - Optional category filter.
 * @param output - List of available AI tools and their metadata.
 */
export const DebugGetToolsFlowSpec = defineFlowSpec({
  name: 'DebugGetTools',
  privileged: true,
  input: z.object({ category: zToolCategory.nullish() }),
  output: DebugGetToolsResponseSchema.passthrough(),
});

export type DebugGetToolsFlowSpec = typeof DebugGetToolsFlowSpec;

/**
 * Schema for agent prompt metadata from the server.
 * Matches the PromptMetaSchema in server/src/agents/AgentConfig.ts
 */
export const AgentPromptMetaSchema = z
  .object({
    id: z.string(),
    type: z.string(),
    name: z.string(),
    fetchedAt: z.string(),
    version: z.string(),
    latestChange: z.string().nullish(),
    latestChangeAuthor: z.string().nullish(),
    labels: z.array(z.string()).nullish(),
    tags: z.array(z.string()).nullish(),
  })
  .strip();

/**
 * Schema for agent debug information.
 * Used by DebugGetAgentStateFlowSpec to return agent metadata.
 */
export const AgentDebugInfoSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  prompt: AgentPromptMetaSchema.optional(),
  systemPrompt: z.string().optional(),
  tools: z.union([z.array(z.string()), z.boolean()]).optional(),
  resourceQueries: z.array(z.any()).optional(),
  model: z.string().optional(),
});

export type AgentDebugInfo = z.infer<typeof AgentDebugInfoSchema>;

/**
 * The contract for the "getAgentState" flow.
 * Returns information about registered AI agents and their configurations.
 * @param output - List of agents with their prompt metadata, tools, and resource queries.
 */
export const DebugGetAgentStateFlowSpec = defineFlowSpec({
  name: 'DebugGetAgentState',
  privileged: true,
  output: z.object({ agents: z.array(AgentDebugInfoSchema) }),
});

export type DebugGetAgentStateFlowSpec = typeof DebugGetAgentStateFlowSpec;

/**
 * Lightweight warmup flow to pre-heat the debugFlows Cloud Function for privileged users.
 * Called after custom claims are refreshed when user is admin/dev.
 */
export const DebugWarmupFlowSpec = defineFlowSpec({
  name: 'DebugWarmup',
  // Pure keepalive, so the transport may resend it once after a network-level failure (ELARA-1146).
  idempotent: true,
  privileged: true,
  output: z.object({ timestamp: z.string(), cold: z.boolean() }),
});
export type DebugWarmupFlowSpec = typeof DebugWarmupFlowSpec;

/**
 * Schema for working memory debug information.
 */
export const WorkingMemoryDebugInfoSchema = z.object({
  threadId: z.string(),
  resourceId: z.string().optional(),
  scope: z.enum(['thread', 'resource']),
  content: z.record(z.string(), z.unknown()).nullable(),
  updatedAt: z.string().optional(),
  error: z.string().optional(),
});

export type WorkingMemoryDebugInfo = z.infer<typeof WorkingMemoryDebugInfoSchema>;

/**
 * The contract for fetching working memory state for a chat thread.
 * Used by the Debug Inspector to display the agent's persistent memory.
 */
export const DebugGetWorkingMemoryFlowSpec = defineFlowSpec({
  name: 'DebugGetWorkingMemory',
  privileged: true,
  input: z.object({ chatId: z.string() }),
  output: WorkingMemoryDebugInfoSchema,
});
export type DebugGetWorkingMemoryFlowSpec = typeof DebugGetWorkingMemoryFlowSpec;

/** One concierge skill as the Mastra runtime discovered it (SKILL.md + Elara metadata). */
export const DebugSkillInfoSchema = z.object({
  name: z.string(),
  description: z.string(),
  /** Working-memory states this skill's playbook owns (empty for topic skills). */
  states: z.array(z.string()),
  /** Specialist tools the playbook body calls (NOT the full set usable in its states). */
  tools: z.array(z.string()),
  bodyChars: z.number(),
  /** The playbook body (what the `skill` tool returns on activation). */
  instructions: z.string(),
});
export type DebugSkillInfo = z.infer<typeof DebugSkillInfoSchema>;

export const DebugGetSkillsResponseSchema = z.object({
  skills: z.array(DebugSkillInfoSchema),
  count: z.number(),
  /** state → owning skill name, for every state covered by a skill. */
  stateCoverage: z.record(z.string(), z.string()),
  /** States still served by the base prompt (no skill yet). */
  uncoveredStates: z.array(z.string()),
});
export type DebugGetSkillsResponse = z.infer<typeof DebugGetSkillsResponseSchema>;

/**
 * The contract for the "getSkills" flow.
 * @param output - The concierge's skill registry as discovered at runtime, plus state coverage.
 */
export const DebugGetSkillsFlowSpec = defineFlowSpec({
  name: 'DebugGetSkills',
  privileged: true,
  input: z.object({}),
  output: DebugGetSkillsResponseSchema.passthrough(),
});

export type DebugGetSkillsFlowSpec = typeof DebugGetSkillsFlowSpec;
